import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ForgotPasswordForm from './ForgotPasswordForm';

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Auto-redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => { if (res.ok) navigate('/dashboard', { replace: true }); })
        .catch(() => {});
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin 
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        if (data.token) {
          localStorage.setItem('token', data.token);
          toast.success('Bienvenido!');
          window.location.href = '/dashboard';
        } else if (data.status === 'success' && data.message === 'VERIFICATION_EMAIL_SENT') {
          setShowVerifyModal(true);
        } else {
          toast.error(data.detail || data.message || 'Error de autenticacion');
        }
      } else {
        const msg = data.detail || '';
        if (msg === 'EMAIL_NOT_VERIFIED') {
          setShowVerifyModal(true);
        } else if (msg === 'EMAIL_ALREADY_EXISTS') {
          toast.error('Este email ya esta registrado. Intenta iniciar sesion.');
        } else {
          toast.error(msg || 'Error de autenticacion');
        }
      }
    } catch (error) {
      toast.error('Error de conexion');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Ingresa el codigo de 6 digitos');
      return;
    }
    setVerifying(true);
    try {
      const response = await fetch(`${API}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code: verifyCode })
      });
      const data = await response.json();
      if (response.ok && data.token) {
        localStorage.setItem('token', data.token);
        toast.success('Email verificado!');
        window.location.href = '/dashboard';
      } else {
        toast.error('Codigo invalido o expirado');
      }
    } catch {
      toast.error('Error de conexion');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/little-zen-frugal.png" alt="ZenSaver" className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">ZenSaver</h1>
          <p className="text-slate-400 text-sm">Frugal V2</p>
        </div>

        {/* Forgot Password Form */}
        {showForgot ? (
          <ForgotPasswordForm
            onBack={() => setShowForgot(false)}
            onSuccess={() => { window.location.href = '/dashboard'; }}
          />
        ) : showVerifyModal ? (
          <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2" data-testid="verify-modal-title">
              Verifica tu email
            </h2>
            <p className="text-slate-400 text-sm mb-1">
              Enviamos un codigo de 6 digitos a:
            </p>
            <p className="text-emerald-400 font-medium mb-4" data-testid="verify-modal-email">
              {form.email}
            </p>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full bg-slate-700/50 text-white text-center text-2xl tracking-[0.5em] font-bold px-4 py-4 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="verify-code-input"
              autoFocus
            />

            <button
              onClick={handleVerifyCode}
              disabled={verifying || verifyCode.length !== 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-3 rounded-xl font-medium transition-colors mb-3"
              data-testid="verify-submit-btn"
            >
              {verifying ? 'Verificando...' : 'Verificar'}
            </button>

            <p className="text-slate-500 text-xs mb-3">
              Revisa tu carpeta de spam si no encuentras el correo.
            </p>

            <button
              type="button"
              onClick={() => { setShowVerifyModal(false); setVerifyCode(''); }}
              className="text-slate-400 text-sm hover:text-white transition-colors"
            >
              Volver
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            {isLogin ? 'Iniciar Sesion' : 'Crear Cuenta'}
          </h2>

          {!isLogin && (
            <input
              type="text"
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-slate-700/50 text-white px-4 py-3 rounded-xl mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required={!isLogin}
            />
          )}

          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-slate-700/50 text-white px-4 py-3 rounded-xl mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
          />

          <input
            type="password"
            placeholder="Contrasena"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-slate-700/50 text-white px-4 py-3 rounded-xl mb-6 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-3 rounded-xl font-medium transition-colors"
          >
            {loading ? 'Cargando...' : (isLogin ? 'Entrar' : 'Crear Cuenta')}
          </button>

          {isLogin && (
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="w-full text-center text-slate-400 text-xs hover:text-emerald-400 transition-colors mt-3"
              data-testid="forgot-password-link"
            >
              Olvidaste tu contrasena?
            </button>
          )}

          <p className="text-center text-slate-400 text-sm mt-4">
            {isLogin ? 'No tienes cuenta?' : 'Ya tienes cuenta?'}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-emerald-400 ml-1 hover:underline"
            >
              {isLogin ? 'Registrate' : 'Inicia Sesion'}
            </button>
          </p>
        </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
